<template>
  <v-container fill-height fill-width grid-list-md>
    <MirrorActivityDetailAppBar :alwaysGoBack="true" class="details">
      <template v-slot:title>
        {{ selectedActivity ? selectedActivity.title : 'Prelim' }}
      </template>
      <template v-slot:updatedDate>
        <span
          class="font-weight-regular"
          :class="loadingUpdateMirror ? 'mirror-updated' : ''"
          >{{ dateChangedMirror }}</span
        >
      </template>
      <template v-slot:actions-right>
        <v-btn
          :large="$vuetify.breakpoint.lgAndUp"
          icon
          class="link no-box-shadow"
          title="copiar texto"
          @click.stop="handleClickChangeVisibilityBody"
        >
          <v-icon>{{
            isVisibilityBodyMirror ? 'mdi-eye-off' : 'mdi-eye'
          }}</v-icon>
        </v-btn>
      </template>
    </MirrorActivityDetailAppBar>

    <div class="content" v-if="!loadingMirror">
      <div class="content-header">
        <div class="d-flex justify-space-between align-center header-info py-2">
          <div
            class="category"
            :class="bgColorClassCategoryType(normalizedType)"
          >
            {{ itemTypeDisplay(normalizedType) }}
          </div>
          <div class="d-flex justify-space-between align-center">
            <v-icon>access_time</v-icon>
            <div class="time">
              {{ formatSeconds(selectedActivity.start_time, true) }}
            </div>
          </div>
        </div>

        <div class="metadata py-2">
          <div class="metadata-item">
            <div>PG</div>
            <div>
              {{ formatField(selectedActivity.page) }}
            </div>
          </div>

          <div class="metadata-item">
            <div>EDI</div>
            <div>
              {{ formatField(selectedActivity.editor) }}
            </div>
          </div>

          <div class="metadata-item">
            <div>tVT</div>
            <div>
              {{ formatSeconds(selectedActivity.tape_time) || emptyField }}
            </div>
          </div>

          <div class="metadata-item">
            <div>tMAT</div>
            <div>
              {{ formatSeconds(selectedActivity.total_time) || emptyField }}
            </div>
          </div>

          <div class="metadata-item">
            <div>LOC</div>
            <div>
              {{ formatField(selectedActivity.presenter) }}
            </div>
          </div>

          <div class="metadata-item">
            <div>Rep</div>
            <div>
              {{ formatField(selectedActivity.reporter) }}
            </div>
          </div>
        </div>
      </div>
      <!-- Fim do Header -->

      <div class="content-body">
        <template v-if="isVisibilityBodyMirror">
          <div class="content-body-item__text" v-if="!hasContentNotesWithBody">
            <p
              v-for="(text, index) in formattedBody"
              :key="index"
              v-html="text"
            ></p>
          </div>
          <template v-else>
            <div
              class="content-body-item__text"
              v-for="(item, index) in notesWithBody"
              :key="index"
            >
              <MirrorTextMarkup
                v-for="(itemNote, indexNote) in item.notes"
                :key="`${itemNote.mark}-${indexNote}`"
                :counter="String(itemNote.mark)"
                :note="itemNote.note"
              />
              <span v-if="hasNote(item)" class="square" />
              <p
                v-for="(text, indexBody) in item.text"
                :key="indexBody"
                v-html="text"
              ></p>
            </div>
          </template>
        </template>
        <template v-else>
          <p
            v-for="(text, index) in formattedBody"
            :key="index"
            v-html="text"
            class="no-production-markup"
          ></p>
        </template>
      </div>
    </div>

    <LoadingOverlay :enabled="loadingMirror" />

    <v-app-bar app bottom hide-on-scroll class="bottom-navigation">
      <v-container>
        <MirrorNavigation
          v-if="selectedActivity"
          :slug="selectedActivity"
          :mirror="filteredMirror"
          @changeSelectedMirrorItem="changeSelectedMirrorItem"
        />
      </v-container>
    </v-app-bar>
  </v-container>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { formatSeconds } from '@/utils/date.js';
import { escapeHTML } from '@/utils/text.js';
import {
  formatField,
  normalizeType,
  itemTypeDisplay,
  bgColorClassCategoryType,
} from '@/utils/mirrorFormatter.js';
import MirrorActivityDetailAppBar from '@/components/mirrors/MirrorActivityDetailAppBar.vue';
import { actions, categories } from '@/constants/analytics.js';
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue';
import MirrorNavigation from '@/components/mirrors/MirrorNavigation.vue';
import MirrorTextMarkup from '@/components/mirrors/MirrorTextMarkup.vue';
import checkAndUpdateSelectedMirror from '../mixins/checkAndUpdateSelectedMirror.mixin';

export default {
  name: 'MirrorActivityDetail',
  mixins: [checkAndUpdateSelectedMirror],
  components: {
    MirrorActivityDetailAppBar,
    LoadingOverlay,
    MirrorNavigation,
    MirrorTextMarkup,
  },
  data() {
    return {
      tab: null,
      emptyField: '--',
    };
  },
  created() {
    this.loadMirrorDetails();
    this.redirectNewFront();
  },

  computed: {
    ...mapGetters('mirror', ['filteredMirror']),
    ...mapState({
      isVisibilityBodyMirror: state => state.mirror.isVisibilityBodyMirror,
      selectedActivity: state => state.mirror.selectedActivity,
      selectedMirror: state => state.mirror.selectedMirror,
      loadingMirror: state => state.mirror.loadingMirror,
      dateChangedMirror: state => state.mirror.dateChangedMirror,
      loadingUpdateMirror: state => state.mirror.loadingUpdateMirror,
    }),
    hasContentNotesWithBody() {
      const { notes_with_body = [] } = this.selectedActivity || {};
      return !!notes_with_body.length;
    },
    notesWithBody() {
      const { notes_with_body } = this.selectedActivity || {};
      let mark = -1;

      return notes_with_body.map(value => {
        const notes = value.notes.map(note => {
          mark = mark + 1;

          return { note, mark: mark };
        });

        return { ...value, notes };
      });
    },
    formattedBody() {
      const { body = '' } = this.selectedActivity || {};
      return body.split('\n');
    },
    normalizedType() {
      return this.selectedActivity
        ? this.normalizeType(this.selectedActivity.item_type)
        : '';
    },
  },
  watch: {
    '$vuetify.breakpoint.smAndUp'() {
      if (this.$vuetify.breakpoint.smAndUp && this.selectedActivity) {
        this.$router.push(
          `/mirror/${this.selectedActivity.place}/${this.selectedActivity.program_tag}`,
        );
      }
    },

    selectedMirror() {
      this.loadMirrorDetails();
    },
  },
  methods: {
    formatField,
    bgColorClassCategoryType,
    normalizeType,
    formatSeconds,
    itemTypeDisplay,
    escapeHTML,
    redirectNewFront() {
      const { place, program_tag } = this.$route.params;
      window.location = `${process.env.VUE_APP_URL_NEW_NH}/mirrors/${place}/${program_tag}`;
    },
    handleClickChangeVisibilityBody() {
      this.$store.dispatch('mirror/setVisibilityChangeBody');
    },

    loadMirrorDetails() {
      this.changeSelectedMirrorItem(this.$route.query.selectedSlugId);
    },

    selectedSlug(slugId) {
      if (!slugId) return {};
      const slugs = this.filteredMirror || [];
      const currentSlug = slugs.find(slug => slug.id === slugId);

      if (currentSlug) return currentSlug;
      return this.filteredMirror[0] || {};
    },

    handleTabClick(item) {
      this.$store.dispatch('analyticsEvent', {
        category: categories.mirror,
        action: actions.activityTabClicked,
        label: `${this.selectedActivity.place}-${this.selectedActivity.program_tag}-${item}`,
      });
    },

    changeSelectedMirrorItem(id) {
      const slug = this.selectedSlug(id);
      this.$store.dispatch('mirror/setSelectedActivity', { slug });
    },

    hasNote(noteWithBody) {
      return !!noteWithBody.notes.length;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/theme.scss';

::v-deep .header-slot.left {
  flex: auto !important;
}

::v-deep .square::before,
::v-deep p.no-production-markup[idref]::before,
::v-deep p.no-production-markup [idref]::before {
  content: '\25A1';
  margin-right: 7px;
  font-size: 20px;
}

.nh-alert-text {
  font-size: 14px !important;
  margin-right: 10px;
}

::v-deep p {
  // Regra responsavel por quebrar o texto, caso ultrapasse o tamanho do container
  word-break: break-all;
}

::v-deep .text-markup {
  width: 100%;
  padding: 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 5px;

  .v-badge {
    .v-badge__badge {
      width: 24px;
      height: 25px;
      border-radius: 5px;
      line-height: 1.5;
    }
  }
}

.container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-content: flex-start;
  width: 100%;
  padding: 0;
  background-color: $color-gray-lighter;

  .bottom-navigation {
    height: 64px !important;
    background-color: $color-white !important;
    z-index: 1;
    width: 100%;
    padding: 0 !important;
    box-shadow: 0 -2px 16px rgba($color-black, 0.16);

    .container {
      background-color: $color-white !important;
    }

    .bottom-navigation-row--title {
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: 'globotipo' !important;
      font-weight: 700 !important;
      font-size: 1em !important;
      text-transform: uppercase;
      color: $color-primary-light;
    }
  }

  .content {
    width: 100%;
    margin: 0;
    padding-top: 88px;

    .content-header {
      width: 100%;
      padding: 10px 16px;
      border-bottom: solid 1px rgba($color-gray-light, 0.4);
      background-color: $color-white;

      .header-info {
        border-bottom: solid 1px rgba($color-gray-light, 0.4);
      }

      .category,
      .metadata {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        height: 100%;
      }

      .category {
        background-color: $color-gray-light;
        padding: 8px;
        border-radius: 4px;
        font-family: 'globotipo' !important;
        font-weight: 600 !important;
        font-style: italic;
        color: $color-white;

        // Estilos para as cores de categorias
        &.category--live {
          background-color: $status-live;
        }
        &.category--studio {
          background-color: $status-studio;
        }
        &.category--vt {
          background-color: $status-vt;
        }
        &.category--screen {
          background-color: $status-screen;
        }
        &.category--others {
          background-color: $status-others;
        }
      }

      .metadata {
        height: 100%;

        .metadata-item {
          flex: 1;

          div {
            font-style: italic;

            &:first-child {
              font-size: 0.8em;
            }
            &:last-child {
              margin: -4px 0 0 0;
              font-size: 1em;
              color: $color-gray-dark;
            }
          }
        }
      }
    }
    // Fim do Header

    .content-body {
      width: 100%;
      padding: 24px 16px 88px 16px;
      background-color: $color-white;

      .content-body-item {
        width: 100%;
        margin: 0 0 40px 0;

        .content-body-item__mark {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;
          margin: 0;
          border-radius: 4px;
          background-color: $color-gray-main;
          font-size: 0.8em;
          font-family: 'globotipo' !important;
          font-weight: 600 !important;
          color: $color-gray-lighter;
        }

        .content-body-item__text {
          width: 100%;
          color: $color-gray-dark;

          &::after,
          &::before {
            content: '---';
            letter-spacing: 0.16em;
            display: block;
            width: 100%;
            height: 8px;
            margin: 0 0 16px 0;
            color: $color-gray-light;
          }
        }
      }
    }
  }

  .next-program {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 72px;
    font-size: 0.88em;
    text-transform: uppercase;
    color: $color-white;
    background-color: $color-gray-dark;
    border-bottom: solid 8px $color-gray-darker;

    .next-program__label,
    .next-program__time {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      height: 100%;
    }

    .next-program__label {
      width: calc(100% - 180px);
      height: 100%;
      padding: 0 16px;
      font-family: 'globotipo' !important;
      font-weight: 600 !important;
      font-style: italic;

      .next-program__label-title {
        width: 100%;
        height: 24px;
        margin: 8px 0 0 0;
        font-size: 1em;
        font-style: normal;
        font-weight: 600;
      }

      .next-program__label-feedback {
        width: 100%;
        height: 16px;
        margin: -20px 0 0 0;
        font-size: 0.8em;
        font-family: 'globotipo' !important;
        font-weight: 500 !important;
        color: rgba($color-gray-light, 0.48);

        i {
          margin: -2px 2px 0 0;
          font-size: 1.4em;
          color: rgba($color-gray-light, 0.48);
        }
      }
    }

    .next-program__time {
      width: 132px;
      font-size: 1.6em;
      font-family: 'globotipo' !important;
      font-weight: 600 !important;
      margin: 0 16px 0 auto;

      i {
        font-size: 1em;
        margin: 4px 4px 0 0;
        color: $color-gray-main;
      }
    }
  }

  footer {
    bottom: 0;
    position: fixed;
    width: 100%;

    .v-card {
      width: 100%;

      .v-card__text {
        font-size: 0.95rem;
      }
      .v-card__actions {
        display: flex;
        justify-content: space-around;

        .v-btn {
          text-transform: initial;
        }
      }
    }
  }
}

.mirror-updated {
  animation: notify-updated-mirror 1s infinite linear;
}

@keyframes notify-updated-mirror {
  0% {
    font-size: 10px;
    color: #707070;
  }

  50% {
    font-size: 12px;
    color: #fa5e15;
  }
}
</style>
